import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    Alert,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import AddCatalogSubjectButton from "./AddCatalogSubjectButton";

import {
    createSubject,
    setSubjectEnabled,
    getSubjectsAdminByClass,
    getSubjectsCatalogAdmin
} from "../../api/subjects";

const SubjectsAdminPanel = ({
    classId,
    onClassIdChange,
    selectedSubjectSlug,
    onSelectSubject,
}) => {
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(false);

    const [catalog, setCatalog] = useState([]);
    const [catalogLoading, setCatalogLoading] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedCatalogSlug, setSelectedCatalogSlug] = useState("");
    const [order, setOrder] = useState(0);
    const [creating, setCreating] = useState(false);

    const [snack, setSnack] = useState({
        open: false,
        severity: "success",
        message: "",
    });

    const selectedCatalogItem = useMemo(() => {
        if (!selectedCatalogSlug) return null;
        return catalog.find((x) => x.subjectSlug === selectedCatalogSlug) || null;
    }, [catalog, selectedCatalogSlug]);

    async function loadSubjects(currentClassId, { silent } = { silent: false }) {
        if (!silent) setLoading(true);
        try {
            const data = await getSubjectsAdminByClass(currentClassId);
            const list = Array.isArray(data) ? data : [];
            setSubjects(list);

            if (onSelectSubject) {
                const exists = list.some((s) => s.subjectSlug === selectedSubjectSlug);
                if (!exists) onSelectSubject(list[0]?.subjectSlug || "");
            }
        } catch (e) {
            const msg =
                e?.response?.data ||
                e?.message ||
                "Не вдалося завантажити список предметів";
            setSnack({ open: true, severity: "error", message: String(msg) });
            setSubjects([]);
        } finally {
            if (!silent) setLoading(false);
        }
    }

    async function loadCatalog({ silent } = { silent: false }) {
        if (!silent) setCatalogLoading(true);
        try {
            const data = await getSubjectsCatalogAdmin();
            const list = Array.isArray(data) ? data : [];
            setCatalog(list);
        } catch (e) {
            const msg =
                e?.response?.data ||
                e?.message ||
                "Не вдалося завантажити каталог предметів";
            setSnack({ open: true, severity: "error", message: String(msg) });
            setCatalog([]);
        } finally {
            if (!silent) setCatalogLoading(false);
        }
    }

    useEffect(() => {
        loadCatalog();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!classId) return;
        loadSubjects(classId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classId]);

    function openDialog() {
        setOrder(0);

        const enabledList = catalog.filter((x) => x.enabled !== false);
        const firstEnabledSlug = enabledList[0]?.subjectSlug || "";

        setSelectedCatalogSlug(firstEnabledSlug);
        setDialogOpen(true);
    }

    function closeDialog() {
        if (!creating) setDialogOpen(false);
    }

    async function onCreate() {
        const slug = String(selectedCatalogSlug || "").trim();
        if (!slug) {
            setSnack({
                open: true,
                severity: "error",
                message: "Оберіть предмет із каталогу",
            });
            return;
        }

        setCreating(true);
        try {
            await createSubject(classId, {
                subjectSlug: slug,
                order: Number(order) || 0,
            });

            setSnack({
                open: true,
                severity: "success",
                message: "Предмет успішно додано до класу",
            });

            setDialogOpen(false);
            await loadSubjects(classId, { silent: true });
        } catch (e) {
            const status = e?.response?.status;
            const msg =
                e?.response?.data || e?.message || "Не вдалося додати предмет";
            setSnack({
                open: true,
                severity: "error",
                message: status ? `${status}: ${msg}` : String(msg),
            });
        } finally {
            setCreating(false);
        }
    }

    async function onToggleEnabled(subjectSlug, next) {
        try {
            await setSubjectEnabled(classId, subjectSlug, next);

            setSubjects((prev) =>
                prev.map((x) =>
                    x.subjectSlug === subjectSlug ? { ...x, enabled: next } : x
                )
            );

            setSnack({
                open: true,
                severity: "success",
                message: next ? "Предмет увімкнено" : "Предмет вимкнено",
            });
        } catch (err) {
            const msg =
                err?.response?.data || err?.message || "Не вдалося змінити статус";
            setSnack({
                open: true,
                severity: "error",
                message: String(msg),
            });
        }
    }

    const selectableCatalog = useMemo(
        () => catalog.filter((x) => x.enabled !== false),
        [catalog]
    );

    return (
        <Container disableGutters maxWidth={false} sx={{ mt: 2 }}>
            <Card variant="outlined">
                <CardContent>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ sm: "center" }}
                        justifyContent="space-between"
                    >
                        <Box>
                            <Typography variant="h5" fontWeight={700}>
                                Предмети
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Керування предметами
                            </Typography>
                        </Box>

                        <Stack direction="row" spacing={2} alignItems="center">
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel id="class-select-label">Клас</InputLabel>
                                <Select
                                    labelId="class-select-label"
                                    label="Клас"
                                    value={classId}
                                    onChange={(e) => onClassIdChange?.(e.target.value)}
                                >
                                    {Array.from({ length: 11 }, (_, i) => String(i + 1)).map(
                                        (v) => (
                                            <MenuItem key={v} value={v}>
                                                {v}
                                            </MenuItem>
                                        )
                                    )}
                                </Select>
                            </FormControl>

                            <AddCatalogSubjectButton onCreated={() => loadCatalog?.()} />

                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={openDialog}
                                disabled={catalogLoading}
                            >
                                Додати
                            </Button>
                        </Stack>
                    </Stack>

                    <Box sx={{ mt: 2 }}>
                        <TableContainer
                            component={Box}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                            }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Назва</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Коротка назва</TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 120 }}>
                                            Порядок
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 140 }}>
                                            Активний
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={4}>
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <CircularProgress size={18} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        Завантаження...
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ) : subjects.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} sx={{ color: "text.secondary" }}>
                                                Список порожній
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        subjects.map((s) => (
                                            <TableRow
                                                key={s.id || `${s.classId}-${s.subjectSlug}`}
                                                onClick={() => onSelectSubject?.(s.subjectSlug)}
                                                sx={{
                                                    cursor: "pointer",
                                                    ...(selectedSubjectSlug === s.subjectSlug
                                                        ? { backgroundColor: "action.hover" }
                                                        : {}),
                                                    ...(!s.enabled ? { opacity: 0.5 } : {}),
                                                }}
                                            >
                                                <TableCell
                                                    sx={
                                                        !s.enabled
                                                            ? { textDecoration: "line-through" }
                                                            : undefined
                                                    }
                                                >
                                                    {s.title}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        component="span"
                                                        variant="body2"
                                                        sx={{ fontFamily: "monospace" }}
                                                    >
                                                        {s.subjectSlug}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{s.order ?? 0}</TableCell>
                                                <TableCell>
                                                    <Switch
                                                        checked={Boolean(s.enabled)}
                                                        onChange={(e) =>
                                                            onToggleEnabled(s.subjectSlug, e.target.checked)
                                                        }
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle>Додати предмет до класу</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="catalog-subject-label">Предмет</InputLabel>
                            <Select
                                labelId="catalog-subject-label"
                                label="Предмет"
                                value={selectedCatalogSlug}
                                onChange={(e) => setSelectedCatalogSlug(e.target.value)}
                                disabled={catalogLoading}
                            >
                                {catalog
                                    .filter((x) => x.enabled !== false)
                                    .map((x) => (
                                        <MenuItem key={x.subjectSlug} value={x.subjectSlug}>
                                            {x.title} ({x.subjectSlug})
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Порядок"
                            type="number"
                            value={order}
                            onChange={(e) => setOrder(e.target.value)}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>


                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeDialog} disabled={creating}>
                        Скасувати
                    </Button>
                    <Button
                        variant="contained"
                        onClick={onCreate}
                        disabled={creating || selectableCatalog.length === 0}
                    >
                        {creating ? <CircularProgress size={18} /> : "Додати"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snack.open}
                autoHideDuration={3500}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnack((s) => ({ ...s, open: false }))}
                    severity={snack.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snack.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default SubjectsAdminPanel;
